import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Label, Card, CardBody, Button, Input } from 'reactstrap';
import axios from 'axios';
import { api } from '../../../../config';
import { toast } from 'react-toastify';
import { DateFormatmmmyyyy } from '../../../../utils/dateformatter';
import ModeDeliveryTableFragment from '../ModelDelivery';
import { PortRestrictionsTable } from '../../../../Components/PortRestriction';
import ClauseCard from './ClauseCard';
import { CounterNegotiationCard } from '../../../../shared/offer-domain/counter';
import PaymentComplianceSection, {
  PAYMENT_RESPOND_KEY,
  COMPLIANCE_RESPOND_KEY,
} from './PaymentComplianceSection';
import useRespondTypeResponse from '../../../../shared/offer-domain/respond/useRespondTypeResponse';
import {
  getVisibleProcurementClauses,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  getOwnClauseResponse,
} from '../../../../shared/offer-domain/respond';
import {
  DetailItem,
  SectionHeader,
  SectionCard,
  LoadingSpinner,
  SectionAlert,
} from '../../../../shared/offer-domain/ui';
import {
  isValidDateValue,
  isValidOperationalToleranceValue,
  OPERATIONAL_TOLERANCE_OPTIONS,
  getOperationalToleranceOptionsCounter,
  resolveOperationalToleranceOption,
  parseOperationalToleranceValue,
  formatDateTime,
  formatOperationalTolerance,
  getCounterDraftValue,
  buildCounterPayload,
  COUNTER_API_FIELD_MAP,
} from '../../../../shared/offer-domain/counter';
import { isOfferDetailReadOnly } from '../../utils/offerEditAccess';
import { formatForFlatpickr } from '../../../../utils/dateformatpickr';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import '../../../../App.css';

const PROPOSAL_VALIDITY_FLATPICKR_OPTIONS = {
  enableTime: true,
  dateFormat: 'd/m/Y H:i',
  time_24hr: true,
};

const createToggleMapKey = setState => key => {
  setState(prev => ({ ...prev, [key]: !prev[key] }));
};

const GLOBAL_NOTES_KEY = 'globalNotes';

const RESPOND_TYPE_BY_KEY = {
  [GLOBAL_NOTES_KEY]: TYPE_RESPONSE_GLOBAL,
  [PAYMENT_RESPOND_KEY]: TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  [COMPLIANCE_RESPOND_KEY]: TYPE_RESPONSE_COUNTER_COMPLIANCE,
};

const RequirementTab = ({
  ids,
  form,
  setForm,
  formMain,
  errors,
  error,
  setErrors,
  isLoading,
  qtyDeliveryData,
  token,
  handleInvitationAccept,
  handleSaveAsDraft,
  fetchDataInvitationValidation,
  clauseResponses = {},
  isClauseResponsesLoading = false,
  isClauseResponsesSaving = false,
  saveClauseResponse,
  deleteClauseResponse,
  refreshProcurement,
}) => {
  const [tableData, setTableData] = useState({});
  const [expandedClauses, setExpandedClauses] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [draftNotes, setDraftNotes] = useState({});
  const [editingCounter, setEditingCounter] = useState(null);
  const [counterDraft, setCounterDraft] = useState({
    operationalTolerance: '',
    operationalToleranceOption: '',
    proposalValidityPeriodMax: '',
  });
  const [savedCounters, setSavedCounters] = useState({
    operationalTolerance: null,
    operationalToleranceOption: null,
    proposalValidityPeriodMax: null,
  });
  const {
    responses: typeResponses,
    isLoading: isTypeResponsesLoading,
    isSaving: isTypeResponsesSaving,
    saveResponse: saveTypeResponse,
    deleteResponse: deleteTypeResponse,
  } = useRespondTypeResponse(token, ids, formMain.id);

  useEffect(() => {
    if (isClauseResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(clauseResponses).forEach(([clauseId, response]) => {
      const own = getOwnClauseResponse(response, { isEvaluator: false });
      if (own?.respondNotes?.trim().length > 0) {
        expanded[clauseId] = true;
        drafts[clauseId] = own.respondNotes;
      }
    });

    if (Object.keys(expanded).length === 0) return;

    setExpandedClauses(prev => ({ ...prev, ...expanded }));
    setDraftNotes(prev => ({ ...prev, ...drafts }));
  }, [clauseResponses, isClauseResponsesLoading]);

  useEffect(() => {
    if (isTypeResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(RESPOND_TYPE_BY_KEY).forEach(([key, typeResponse]) => {
      const notes = typeResponses[typeResponse]?.respondNotes;
      if (!notes?.trim()) return;
      expanded[key] = true;
      drafts[key] = notes;
    });

    if (Object.keys(expanded).length === 0) return;

    setExpandedClauses(prev => ({ ...prev, ...expanded }));
    setDraftNotes(prev => ({ ...prev, ...drafts }));
  }, [isTypeResponsesLoading, typeResponses]);

  useEffect(() => {
    setSavedCounters(prev => ({
      ...prev,
      operationalTolerance:
        form.operationalToleranceCounter == null ||
        form.operationalToleranceCounter === ''
          ? null
          : parseOperationalToleranceValue(form.operationalToleranceCounter),
      operationalToleranceOption: resolveOperationalToleranceOption({
        label: form.operationalToleranceOptionsCounterLabel,
        optionsCounter: form.operationalToleranceOptionsCounter,
      }),
      proposalValidityPeriodMax: isValidDateValue(
        form.proposalValidityPeriodMaxCounter
      )
        ? form.proposalValidityPeriodMaxCounter
        : null,
    }));
  }, [
    form.operationalToleranceCounter,
    form.operationalToleranceOptionsCounter,
    form.operationalToleranceOptionsCounterLabel,
    form.proposalValidityPeriodMaxCounter,
  ]);

  useEffect(() => {
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;

      if (
        savedCounters.operationalTolerance != null &&
        next.operationalTolerance === undefined
      ) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (
        savedCounters.proposalValidityPeriodMax != null &&
        next.proposalValidityPeriodMax === undefined
      ) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [savedCounters]);

  const handleDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const toggleClauseExpand = createToggleMapKey(setExpandedClauses);
  const toggleSectionExpand = createToggleMapKey(setExpandedSections);

  const handleClauseSave = async clauseId => {
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!respondNotes) {
      toast.warning('Response note cannot be empty');
      return;
    }

    try {
      const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
      const saved = typeResponse
        ? await saveTypeResponse(typeResponse, respondNotes)
        : await saveClauseResponse(clauseId, respondNotes);
      const syncedNotes = saved?.respondNotes ?? respondNotes;
      setExpandedClauses(prev => ({ ...prev, [clauseId]: true }));
      setDraftNotes(prev => ({ ...prev, [clauseId]: syncedNotes }));
      toast.success('Response note has been successfully saved');
    } catch {
      toast.error('Failed to save response note');
    }
  };

  const handleClauseCancel = clauseId => {
    const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
    const saved = typeResponse
      ? (typeResponses[typeResponse]?.respondNotes ?? '')
      : (getOwnClauseResponse(clauseResponses[clauseId], {
          isEvaluator: false,
        })?.respondNotes ?? '');
    setDraftNotes(prev => ({ ...prev, [clauseId]: saved }));
    if (!saved.trim()) {
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
    }
  };

  const handleClauseClear = async clauseId => {
    try {
      const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
      if (typeResponse) {
        await deleteTypeResponse(typeResponse);
      } else {
        await deleteClauseResponse(clauseId);
      }
      setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
      toast.success('Response note has been successfully deleted');
    } catch {
      toast.error('Failed to delete response note');
    }
  };

  const handleClauseSecondaryAction = (clauseId, hasSaved) => {
    if (hasSaved) {
      handleClauseClear(clauseId);
    } else {
      handleClauseCancel(clauseId);
    }
  };

  const handleTableDataChange = useCallback((quantityDeliveryTermId, data) => {
    setTableData(prev => ({ ...prev, [quantityDeliveryTermId]: data }));
  }, []);

  const handleQuantityDelivery = async () => {
    const finalDataToSave = Object.values(tableData).flat();
    const payload = { listDyedUndyedAlpha: finalDataToSave };

    try {
      await axios.post(`${api.API_URL}/dmut/set-dyed-undyed`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      void error;
    }
  };

  const validateAndExecute = async actionFn => {
    let isValid = false;
    try {
      isValid = await fetchDataInvitationValidation(ids, token);
    } catch {
      return;
    }

    if (isValid) {
      toast.warning('Data Already submitted offer');
    } else {
      try {
        await actionFn();
      } catch (error) {
        void error;
      }
      try {
        await handleQuantityDelivery();
      } catch (error) {
        void error;
      }
    }
  };

  const handleExecuteDraft = () => validateAndExecute(handleSaveAsDraft);
  const handleExecuteOffer = () => validateAndExecute(handleInvitationAccept);

  const visibleClauses = getVisibleProcurementClauses({
    procurementClauseDetails: form.procurementClauseDetails,
  });

  const isReadOnly = isOfferDetailReadOnly({
    statusOfferingWordingFlag: formMain.statusOfferingWordingFlag,
    statusOfferingWording: formMain.statusOfferingWording,
    submissionStatus: formMain.submissionStatus,
  });

  const startCounterEdit = field => {
    setEditingCounter(field);
    setCounterDraft(prev => ({
      ...prev,
      ...getCounterDraftValue({ field, form, savedCounters }),
    }));
  };

  const cancelCounterEdit = () => {
    setEditingCounter(null);
  };

  const resolveOperationalToleranceCounter = () => {
    const raw =
      form.operationalToleranceCounter ?? savedCounters.operationalTolerance;
    if (raw == null || raw === '') return null;
    return parseOperationalToleranceValue(raw);
  };

  const resolveProposalValidityPeriodMaxCounter = () => {
    const raw =
      form.proposalValidityPeriodMaxCounter ??
      savedCounters.proposalValidityPeriodMax;
    if (!isValidDateValue(raw)) return null;
    return new Date(raw).toISOString();
  };

  const putProcurementCounter = payload =>
    axios.put(
      `${api.API_URL}/procurement/update-procurement-counter/${ids}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

  const handleSaveCounter = async field => {
    const rawValue = String(counterDraft[field] ?? '').trim();
    if (!rawValue) {
      toast.warning('Counter value cannot be empty');
      return;
    }

    if (!ids) {
      toast.error('Procurement ID not found');
      return;
    }

    let value;

    if (field === 'proposalValidityPeriodMax') {
      const parsedDate = new Date(rawValue);
      if (Number.isNaN(parsedDate.getTime())) {
        toast.warning('Invalid date format');
        return;
      }
      value = parsedDate.toISOString();
    } else {
      const parsedTolerance = parseOperationalToleranceValue(rawValue);
      if (parsedTolerance == null) {
        toast.warning('Invalid operational tolerance format');
        return;
      }
      if (!String(counterDraft.operationalToleranceOption ?? '').trim()) {
        toast.warning('Operational Tolerance Option is required');
        return;
      }
      value = parsedTolerance;
    }

    const payload = buildCounterPayload({
      field,
      value,
      operationalToleranceCounter: resolveOperationalToleranceCounter(),
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? getOperationalToleranceOptionsCounter(
              counterDraft.operationalToleranceOption
            )
          : getOperationalToleranceOptionsCounter(
              savedCounters.operationalToleranceOption
            ),
      proposalValidityPeriodMaxCounter:
        resolveProposalValidityPeriodMaxCounter(),
    });
    if (!payload) return;

    try {
      await putProcurementCounter(payload);

      const localOption =
        field === 'operationalTolerance'
          ? counterDraft.operationalToleranceOption
          : undefined;

      if (refreshProcurement) {
        await refreshProcurement();
        if (localOption !== undefined) {
          setSavedCounters(prev => ({
            ...prev,
            operationalToleranceOption: localOption,
          }));
        }
      } else {
        setSavedCounters(prev => ({
          ...prev,
          [field]: value,
          ...(localOption !== undefined
            ? { operationalToleranceOption: localOption }
            : {}),
        }));
      }

      setEditingCounter(null);
      toast.success('Counter offer saved successfully');
    } catch {
      toast.error('Failed to save counter offer');
    }
  };

  const handleWithdrawCounter = async field => {
    if (!ids) {
      toast.error('Procurement ID not found');
      return;
    }

    if (!COUNTER_API_FIELD_MAP[field]) return;

    const payload = buildCounterPayload({
      field,
      value: null,
      operationalToleranceCounter: resolveOperationalToleranceCounter(),
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? null
          : getOperationalToleranceOptionsCounter(
              savedCounters.operationalToleranceOption
            ),
      proposalValidityPeriodMaxCounter:
        resolveProposalValidityPeriodMaxCounter(),
    });
    if (!payload) return;

    const apiField = COUNTER_API_FIELD_MAP[field];

    try {
      await putProcurementCounter(payload);

      if (refreshProcurement) {
        await refreshProcurement();
        if (field === 'operationalTolerance') {
          setSavedCounters(prev => ({
            ...prev,
            operationalToleranceOption: null,
          }));
        }
      } else {
        setSavedCounters(prev => ({
          ...prev,
          [field]: null,
          ...(field === 'operationalTolerance'
            ? { operationalToleranceOption: null }
            : {}),
        }));
        setForm?.(prev => ({
          ...prev,
          [apiField]: null,
        }));
      }

      setCounterDraft(prev => ({
        ...prev,
        [field]: '',
        ...(field === 'operationalTolerance'
          ? { operationalToleranceOption: '' }
          : {}),
      }));
      setEditingCounter(null);
      setExpandedSections(prev => ({ ...prev, [field]: false }));
      toast.success('Counter offer withdrawn successfully');
    } catch {
      toast.error('Failed to withdraw counter offer');
    }
  };

  const renderHeader = () => (
    <Row className='mb-3'>
      <Col sm={12}>
        <div className='d-flex align-items-center mb-2'>
          <div className='flex-grow-1'>
            <h5 className='mb-1'>Information</h5>
            <p className='text-muted mb-2 fs-12'>
              Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
            </p>
          </div>
        </div>
      </Col>
    </Row>
  );

  const renderIdentityAlert = () => (
    <Row className='mb-4'>
      <Col sm={12}>
        <SectionAlert
          tone='info'
          title='Company Identity Masked'
          className='mb-0'
        >
          <div className='text-muted small'>
            Your company name will be masked and kept confidential until the
            tender evaluation process is completed. This is to ensure fair and
            unbiased assessment by all parties involved.
          </div>
        </SectionAlert>
      </Col>
    </Row>
  );

  const renderMainDetails = () => {
    const isSpot = parseInt(form?.contractType, 10) === 2;
    const periodLabel = isSpot ? 'Period' : 'Contract Period';
    const periodValue = isSpot
      ? DateFormatmmmyyyy(
          form.contractPeriodSpot || form.contractPeriodTermStart
        )
      : form.contractPeriodTermEnd
        ? `${DateFormatmmmyyyy(form.contractPeriodTermStart) || ''} - ${DateFormatmmmyyyy(form.contractPeriodTermEnd)}`
        : `${DateFormatmmmyyyy(form.contractPeriodTermStart) || ''}`;

    return (
      <>
        <SectionHeader
          title='Offer Details'
          subtitle='Buyer, seller, and contract period information'
        />
        <Row className='gy-4'>
          <Col lg={4}>
            <DetailItem label='Buyer' value='PT Pertamina Patra Niaga' />
          </Col>
          <Col lg={4}>
            <DetailItem label='Seller' value={formMain.companyName ?? '-'} />
          </Col>
          <Col lg={4}>
            <DetailItem label={periodLabel} value={periodValue || '-'} />
          </Col>
        </Row>
      </>
    );
  };

  const renderProductInfo = () => (
    <>
      <SectionHeader
        title='Product Specification/ Quantity'
        subtitle='Review product specification and quantity'
      />
      <Row className='gy-4'>
        <Col lg={4}>
          <DetailItem label='Product Name' value={form.productName} />
        </Col>
        <Col lg={4}>
          <DetailItem label='Product Type' value={form.productTypename} />
        </Col>
        <Col lg={12}>
          <DetailItem
            label='Product Specification'
            value={form.productSpecification}
            isHtml
          />
        </Col>
      </Row>
    </>
  );

  const renderCounterToleranceSection = () => (
    <Row className='mb-4'>
      <Col sm={12}>
        <CounterNegotiationCard
          title='Operational Tolerance'
          subtitle='Review operational tolerance requirements'
          proposeCounterLabel='Propose Counter Value Operation Tolerance (±)'
          partyType={form.partyType}
          label='Operational Tolerance'
          currentDisplay={formatOperationalTolerance(form.operationalTolerance)}
          counterDisplay={
            isValidOperationalToleranceValue(savedCounters.operationalTolerance)
              ? formatOperationalTolerance(savedCounters.operationalTolerance)
              : null
          }
          partyTypeCounter={savedCounters.operationalToleranceOption || null}
          isExpanded={Boolean(expandedSections['operationalTolerance'])}
          onToggleExpand={() => toggleSectionExpand('operationalTolerance')}
          isEditing={editingCounter === 'operationalTolerance'}
          editable={!isReadOnly}
          isDraftDirty={
            String(counterDraft.operationalTolerance) !==
              String(savedCounters.operationalTolerance ?? '') ||
            String(counterDraft.operationalToleranceOption) !==
              String(savedCounters.operationalToleranceOption ?? '')
          }
          onStartEdit={() => startCounterEdit('operationalTolerance')}
          onCancel={cancelCounterEdit}
          onSave={() => handleSaveCounter('operationalTolerance')}
          onDelete={() => handleWithdrawCounter('operationalTolerance')}
        >
          <div className='d-flex flex-column gap-4 p-1'>
            <div>
              <Label
                htmlFor='customToleranceInput'
                className='form-label text-muted small fw-semibold mb-2'
              >
                Counter Operation Tolerance (±)
              </Label>
              <div
                className='input-group input-group-sm'
                style={{ maxWidth: '240px' }}
              >
                <Input
                  id='customToleranceInput'
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='0.00'
                  value={counterDraft.operationalTolerance}
                  onChange={e =>
                    setCounterDraft(prev => ({
                      ...prev,
                      operationalTolerance: e.target.value,
                    }))
                  }
                  className='text-dark fw-medium'
                />
                <span className='input-group-text bg-light text-muted fw-semibold px-3'>
                  %
                </span>
              </div>
            </div>

            <hr className='text-muted my-0 opacity-25' />

            <div>
              <Label className='form-label text-muted small fw-semibold mb-2'>
                Counter Operation Tolerance Options
              </Label>
              <div className='d-flex flex-wrap gap-2'>
                {OPERATIONAL_TOLERANCE_OPTIONS.map(option => {
                  const isChecked =
                    counterDraft.operationalToleranceOption === option.value;

                  return (
                    <div
                      key={option.value}
                      className={`border rounded-3 px-3 py-2 mb-0 d-flex align-items-center gap-2 transition-all ${
                        isChecked
                          ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                          : 'border-light bg-light text-dark fw-medium'
                      }`}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() =>
                        setCounterDraft(prev => ({
                          ...prev,
                          operationalToleranceOption: option.value,
                        }))
                      }
                    >
                      <Input
                        className='form-check-input ms-0 mt-0 flex-shrink-0'
                        type='radio'
                        id={`operationalToleranceOption-${option.value}`}
                        name='operationalToleranceOption'
                        value={option.value}
                        checked={isChecked}
                        onChange={e =>
                          setCounterDraft(prev => ({
                            ...prev,
                            operationalToleranceOption: e.target.value,
                          }))
                        }
                        style={{ cursor: 'pointer' }}
                      />
                      <Label
                        className='form-check-label fs-13 mb-0 text-current cursor-pointer'
                        htmlFor={`operationalToleranceOption-${option.value}`}
                        style={{ cursor: 'pointer' }}
                      >
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CounterNegotiationCard>
      </Col>
    </Row>
  );

  const renderCounterValidToSection = () => (
    <Row className='mb-4'>
      <Col sm={12}>
        <CounterNegotiationCard
          title='Validity Period'
          subtitle='Review validity period requirements'
          proposeCounterLabel='Propose Counter Value Validity To (Jakarta Local Time)'
          label='Valid To (Jakarta Local Time)'
          currentDisplay={formatDateTime(form.proposalValidityPeriodMax)}
          extraFields={[
            {
              label: 'Submission Deadline (Jakarta Local Time)',
              value: formatDateTime(form.proposalSubmissionDeadline),
            },
          ]}
          counterDisplay={
            isValidDateValue(savedCounters.proposalValidityPeriodMax)
              ? formatDateTime(savedCounters.proposalValidityPeriodMax)
              : null
          }
          isExpanded={Boolean(expandedSections['proposalValidityPeriodMax'])}
          onToggleExpand={() =>
            toggleSectionExpand('proposalValidityPeriodMax')
          }
          isEditing={editingCounter === 'proposalValidityPeriodMax'}
          editable={!isReadOnly}
          isDraftDirty={
            String(counterDraft.proposalValidityPeriodMax) !==
            String(savedCounters.proposalValidityPeriodMax ?? '')
          }
          onStartEdit={() => startCounterEdit('proposalValidityPeriodMax')}
          onCancel={cancelCounterEdit}
          onSave={() => handleSaveCounter('proposalValidityPeriodMax')}
          onDelete={() => handleWithdrawCounter('proposalValidityPeriodMax')}
        >
          <Flatpickr
            className='form-control form-control-sm'
            options={PROPOSAL_VALIDITY_FLATPICKR_OPTIONS}
            value={
              formatForFlatpickr(counterDraft.proposalValidityPeriodMax) || ''
            }
            onChange={dates => {
              setCounterDraft(prev => ({
                ...prev,
                proposalValidityPeriodMax: dates[0]?.toISOString() ?? '',
              }));
            }}
          />
        </CounterNegotiationCard>
      </Col>
    </Row>
  );

  const renderQuantityDeliverySection = () => (
    <SectionCard>
      <SectionHeader
        title='Quantity & Delivery Time'
        subtitle='Review delivery requirements and submit your offer for each cargo'
      />

      <SectionAlert title='Counter Offer Impact!'>
        <div className='text-muted small'>
          You may propose modified terms (counter offer) specifications that
          differ from the original requirements. Please note that any deviation
          from the buyer&apos;s requirements will be taken into consideration
          during the tender evaluation process.
        </div>
      </SectionAlert>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className='alert alert-danger text-center mb-0'>{error}</div>
      ) : qtyDeliveryData.items.length > 0 ? (
        <div className='d-flex flex-column gap-4'>
          {qtyDeliveryData.items.map((data, index) => (
            <Card
              className='border shadow-sm mb-0 overflow-hidden'
              key={`${data.id}-${index}`}
            >
              <div className='bg-light border-bottom px-4 py-3'>
                <h6 className='fw-semibold text-dark mb-0'>
                  Delivery Unit:{' '}
                  {data.deliveryUnit ||
                    `Cargo ${String.fromCharCode(65 + index)}`}
                </h6>
              </div>
              <CardBody className='p-0'>
                <ModeDeliveryTableFragment
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  error={error}
                  setErrors={setErrors}
                  quantityDeliveryTermId={data.id}
                  token={token}
                  onDataChange={handleTableDataChange}
                  formMain={formMain}
                  embedded
                />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className='text-center py-5 text-muted border rounded-3 bg-light-subtle'>
          No data available
        </div>
      )}

      <hr className='my-4 border-secondary border-dashed' />

      <Row>
        <ClauseCard
          clause={{
            id: GLOBAL_NOTES_KEY,
            clauseTitle: 'Notes',
            clauseDescription: form?.globalNotes,
          }}
          clauseResponses={{
            [GLOBAL_NOTES_KEY]: typeResponses[TYPE_RESPONSE_GLOBAL],
          }}
          draftNotes={draftNotes}
          expandedClauses={expandedClauses}
          formMain={formMain}
          isClauseResponsesSaving={isTypeResponsesSaving}
          isClauseResponsesLoading={isTypeResponsesLoading}
          handleDraftChange={handleDraftChange}
          handleClauseSecondaryAction={handleClauseSecondaryAction}
          handleClauseSave={handleClauseSave}
          toggleClauseExpand={toggleClauseExpand}
          canRespond={!isReadOnly}
        />
      </Row>

      <hr className='my-4 border-secondary border-dashed' />

      <div className='mt-2'>
        <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
        <PortRestrictionsTable
          ports={form?.portRestriction}
          productId={form?.productNameId}
        />
      </div>
    </SectionCard>
  );

  const renderClauseSection = () => (
    <SectionCard>
      <SectionHeader
        title='Clause Details'
        subtitle='Review procurement clauses and provide your proposal notes'
        countLabel={`${visibleClauses.length} clauses`}
      />

      <SectionAlert title='Specification Compliance Notice'>
        <div className='text-muted small'>
          Any information that does not align with Pertamina&apos;s established
          standards may be subject to further evaluation and acceptance at
          Pertamina&apos;s discretion.
        </div>
      </SectionAlert>

      {visibleClauses.length > 0 ? (
        <Row>
          {visibleClauses.map((clause, index) => (
            <ClauseCard
              key={clause.id}
              index={index + 1}
              clause={clause}
              clauseResponses={clauseResponses}
              draftNotes={draftNotes}
              expandedClauses={expandedClauses}
              formMain={formMain}
              isClauseResponsesSaving={isClauseResponsesSaving}
              isClauseResponsesLoading={isClauseResponsesLoading}
              handleDraftChange={handleDraftChange}
              handleClauseSecondaryAction={handleClauseSecondaryAction}
              handleClauseSave={handleClauseSave}
              toggleClauseExpand={toggleClauseExpand}
              canRespond={!isReadOnly}
            />
          ))}
        </Row>
      ) : (
        <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
          No clause data available.
        </div>
      )}
    </SectionCard>
  );

  return (
    <React.Fragment>
      {renderHeader()}

      {renderIdentityAlert()}

      <SectionCard>
        {renderMainDetails()}
        <hr className='my-4 border-secondary' />
        {renderProductInfo()}
      </SectionCard>

      {renderCounterValidToSection()}

      {renderCounterToleranceSection()}

      {renderQuantityDeliverySection()}

      {renderClauseSection()}

      <PaymentComplianceSection
        form={form}
        token={token}
        editable={!isReadOnly}
        canRespond={!isReadOnly}
        formMain={formMain}
        refreshProcurement={refreshProcurement}
        typeResponses={{
          [PAYMENT_RESPOND_KEY]:
            typeResponses[TYPE_RESPONSE_COUNTER_PAYMENT_TERM],
          [COMPLIANCE_RESPOND_KEY]:
            typeResponses[TYPE_RESPONSE_COUNTER_COMPLIANCE],
        }}
        draftNotes={draftNotes}
        expandedClauses={expandedClauses}
        isRespondSaving={isTypeResponsesSaving}
        isRespondLoading={isTypeResponsesLoading}
        handleDraftChange={handleDraftChange}
        handleClauseSecondaryAction={handleClauseSecondaryAction}
        handleClauseSave={handleClauseSave}
        toggleClauseExpand={toggleClauseExpand}
      />

      {!isReadOnly && (
        <div className='req-actionbar d-print-none'>
          <Button
            type='button'
            color='success'
            className='d-flex align-items-center gap-2'
            onClick={handleExecuteDraft}
          >
            <i className='bx bx-save'></i>
            <span>Save as Draft</span>
          </Button>
          <Button
            type='button'
            color='primary'
            className='d-flex align-items-center gap-2'
            onClick={handleExecuteOffer}
          >
            <i className='bx bx-send'></i>
            <span>Submit Offer</span>
          </Button>
        </div>
      )}
    </React.Fragment>
  );
};

export default RequirementTab;
